import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import FetchClients from "../client";
import { throwBackpackResponseError } from "../errors";

const notes = {
  mutations: { usePost, useDelete },
} as const;

export default notes;

/**
 * Creates a property note. The author is derived from the JWT, so only the note
 * body is sent. Returns the new note's id — the full row is not echoed back.
 *
 * endpoint requires {feature: "Building Overview", access: "edit"}
 */
export async function createSiteNote(
  siteId: number,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.site.POST("/{site_id}/notes", {
    params: { path: { site_id: siteId } },
    body: { note },
    headers: checkFeatureAccessHeader,
  });
  // Response is a bare id, so a truthiness check would reject a legitimate 0
  if (typeof data === "number") return data;
  throwBackpackResponseError(error);
  throw new Error("Failed to save note");
}

// endpoint requires {feature: "Building Overview", access: "edit"}
export async function deleteSiteNote(
  siteId: number,
  noteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.site.DELETE(
    "/{site_id}/notes/{note_id}",
    {
      params: { path: { site_id: siteId, note_id: noteId } },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) throwBackpackResponseError(error);
}

/**
 * Property notes are held in component state rather than refetched, because the
 * control panel query they arrive on is slow. Callers apply the result via
 * `onSuccess` instead of invalidating a query.
 */
function usePost(
  siteId: number,
  { onSuccess }: { onSuccess: (noteId: number, note: string) => void }
) {
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (note: string, checkFeatureAccessHeader) =>
      createSiteNote(siteId, note, checkFeatureAccessHeader),
    { onSuccess }
  );
}

/** See `usePost` — deletions likewise update caller state rather than refetching. */
function useDelete(
  siteId: number,
  { onSuccess }: { onSuccess: (data: void, noteId: number) => void }
) {
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (noteId: number, checkFeatureAccessHeader) =>
      deleteSiteNote(siteId, noteId, checkFeatureAccessHeader),
    { onSuccess }
  );
}
